import { useQuery, QueryFunction } from '@tanstack/react-query'
import { useFetchWithAuth } from '../session/useFetchWithAuth'
import { Event } from './types'
import { useCurrentUser } from './useCurrentUser'

const URL = import.meta.env.VITE_EPC_API_URL ?? ''

/**
 * Hook to fetch a single event by ID with detailed information
 *
 * The API returns an Event object directly with all relevant information included:
 * - Event details (name, description, dates, etc.)
 * - Organizer information
 * - Related invitation data
 *
 * @param eventId The ID of the event to fetch
 * @returns Event object with all related data
 */
export const useEvent = (eventId?: string) => {
  const fetchWithAuth = useFetchWithAuth()
  const { data: userData } = useCurrentUser()
  const userId = userData?.userId || ''

  const getEvent: QueryFunction<Event> = async () => {
    if (!eventId) {
      throw new Error('Event ID is required to fetch event details')
    }

    // Fetch event data from API
    const response = await fetchWithAuth(`${URL}/events/${eventId}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    })
    console.log('useEvent response:', response)
    return response
  }

  return useQuery({
    queryKey: ['event', eventId, userId] as const,
    queryFn: getEvent,
    retry: false,
    refetchInterval: 60 * 1000, // Poll every 60 seconds
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    enabled: !!eventId && !!userId && eventId.length > 0,
  })
}
